import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAssistant } from './AssistantProvider';
import { SendIcon } from './icons';
import { MAX_MESSAGE_LENGTH } from './errors';

// Show the character counter once the draft gets close to the limit.
const COUNTER_THRESHOLD = MAX_MESSAGE_LENGTH - 500;

/*
 * Composer: auto-growing textarea (max 110px), navy send button (disabled when
 * empty or while a turn is active). Enter sends, Shift+Enter inserts a
 * newline. Hint below the row.
 */
const Composer = () => {
  const { t } = useTranslation('assistant');
  const { send, restoredDraft, turnActive } = useAssistant();
  const [value, setValue] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  const grow = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 110) + 'px';
  }, []);

  // A send failed: put its text back unless the user already typed something new.
  useEffect(() => {
    if (!restoredDraft) return;
    setValue((current) => (current.trim() ? current : restoredDraft.text));
    requestAnimationFrame(grow);
  }, [restoredDraft, grow]);

  const submit = useCallback(() => {
    if (turnActive) return;
    const text = value.trim();
    if (!text) return;
    send(text);
    setValue('');
    requestAnimationFrame(() => {
      if (taRef.current) taRef.current.style.height = 'auto';
    });
  }, [value, send, turnActive]);

  return (
    <div className="bra-comp">
      <div className="bra-crow">
        <textarea
          ref={taRef}
          rows={1}
          placeholder={t('Ask about a company, compliance, or your portfolio…')}
          value={value}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => {
            setValue(e.target.value);
            grow();
          }}
          onKeyDown={(e) => {
            // Don't submit while an IME composition is active (e.g. CJK input).
            if (
              e.key === 'Enter' &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          type="button"
          className="bra-send"
          disabled={!value.trim() || turnActive}
          aria-label={t('Send')}
          onClick={submit}
        >
          <SendIcon />
        </button>
      </div>
      {value.length >= COUNTER_THRESHOLD ? (
        <div
          className={`bra-ccount${
            value.length >= MAX_MESSAGE_LENGTH ? ' bra-ccount-max' : ''
          }`}
          aria-live="polite"
        >
          {value.length} / {MAX_MESSAGE_LENGTH}
        </div>
      ) : null}
      <div className="bra-chint">
        {t(
          'The assistant proposes actions — nothing runs without your confirmation.'
        )}
      </div>
    </div>
  );
};

export default Composer;
